/**
 * Centralised Fair Fresh Start (FFS) status pill helpers (T2-6360 / T2-6374).
 *
 * Before FFS, each AP view rolled its own `getStatusColor` keyed on the legacy
 * `Hygienist.status` integer (-4..2). FFS introduces string codes
 * (MembershipManagement.currentStatusCode) that those maps don't know about
 * (Provisional, RL1/RL2, FL1/FL2, combined RL/FL, CourseRequired, Suspended),
 * so they silently fell through to grey.
 *
 * This module is the single source of truth for FFS chip colour + label + icon.
 * It accepts canonical string codes only. Legacy integers deliberately render
 * as unknown so older payloads cannot regain presentation authority.
 */

export type FfsStatusCode =
  | "Elite"
  | "Pro"
  | "Active"
  | "RL1"
  | "FL1"
  | "RL1FL1"
  | "RL2"
  | "FL2"
  | "RL2FL1"
  | "RL1FL2"
  | "RL2FL2"
  | "CourseRequired"
  | "Provisional"
  | "Probation"
  | "Suspended"
  | "Revoked"
  | "Blacklisted"
  | "Deactivated"
  | "On a Break"
  // T2-6516: AP-facing recovery overlay display codes. These are shown when a
  // Talent has an ACTIVE MembershipRecoveryState overlay, taking precedence over
  // the durable MembershipManagement tier. "On a Break" (above) is shared.
  | "Course Required"
  | "Restricted Re-entry"
  | "Not a Fit";

// Material/TempStars palette. Restriction tiers reuse their base-tier colour and
// rely on a warning icon (see getFfsStatusIcon) to telegraph severity, rather
// than inventing new hues.
const COLOR_ACTIVE = "#2196F3"; // blue
const COLOR_PRO = "#9C27B0"; // purple
const COLOR_ELITE = "#FF9800"; // orange
const COLOR_PROBATION = "#FB7672"; // coral (TempStars secondary)
const COLOR_COURSE_REQUIRED = "#FFC107"; // amber — "action required"
const COLOR_ON_A_BREAK = "#E64A19"; // deep orange — temporary support hold
const COLOR_RESTRICTED_REENTRY = "#607D8B"; // blue-grey — recovery re-entry (T2-6516)
const COLOR_NOT_A_FIT = "#D32F2F"; // red — terminal recovery outcome (T2-6516)
const COLOR_SUSPENDED = "#B71C1C"; // dark red — severe
const COLOR_REVOKED = "#FB7672"; // coral, same as Probation per T2-6374
const COLOR_PROVISIONAL = "#9E9E9E"; // grey — "insufficient data yet"
const COLOR_DEACTIVATED = "#9E9E9E"; // grey
const COLOR_BLACKLISTED = "#000000"; // black
const COLOR_FALLBACK = "#9E9E9E"; // grey fallback

const STATUS_COLORS: Record<string, string> = {
  Elite: COLOR_ELITE,
  Pro: COLOR_PRO,
  Active: COLOR_ACTIVE,
  // Mild restriction — ranks with Active.
  RL1: COLOR_ACTIVE,
  FL1: COLOR_ACTIVE,
  RL1FL1: COLOR_ACTIVE,
  // Severe restriction — ranks with Probation.
  RL2: COLOR_PROBATION,
  FL2: COLOR_PROBATION,
  RL2FL1: COLOR_PROBATION,
  RL1FL2: COLOR_PROBATION,
  RL2FL2: COLOR_PROBATION,
  CourseRequired: COLOR_COURSE_REQUIRED,
  "On a Break": COLOR_ON_A_BREAK,
  // T2-6516: recovery overlay display codes.
  "Course Required": COLOR_COURSE_REQUIRED,
  "Restricted Re-entry": COLOR_RESTRICTED_REENTRY,
  "Not a Fit": COLOR_NOT_A_FIT,
  Provisional: COLOR_PROVISIONAL,
  Probation: COLOR_PROBATION,
  Suspended: COLOR_SUSPENDED,
  Revoked: COLOR_REVOKED,
  Blacklisted: COLOR_BLACKLISTED,
  Deactivated: COLOR_DEACTIVATED
};

// Codes that should render a mild vs strong warning accent icon.
const MILD_WARNING = new Set(["RL1", "FL1", "RL1FL1"]);
const STRONG_WARNING = new Set([
  "RL2",
  "FL2",
  "RL2FL1",
  "RL1FL2",
  "RL2FL2",
  "CourseRequired",
  "Probation",
  "Suspended",
  "Revoked",
  // T2-6516: recovery overlay display codes that warrant a strong warning accent.
  "Course Required",
  "Restricted Re-entry"
]);

/**
 * T2-6516: Map an active MembershipRecoveryState.overlayState (as sent by the
 * API — `course_required`, `restricted_reentry`, `on_a_break`, `not_a_fit`) to
 * the AP-facing display code used by the colour/label/icon maps above. Returns
 * null for any value that is not one of the four support-facing recovery states
 * so callers fall back to the durable FFS tier.
 */
const RECOVERY_OVERLAY_DISPLAY_CODES: Record<string, string> = {
  course_required: "Course Required",
  restricted_reentry: "Restricted Re-entry",
  on_a_break: "On a Break",
  not_a_fit: "Not a Fit"
};

export function recoveryOverlayToDisplayCode(
  overlayState: string | null | undefined
): string | null {
  if (!overlayState) return null;
  return RECOVERY_OVERLAY_DISPLAY_CODES[overlayState] || null;
}

/**
 * Resolve dashboard membership presentation from canonical server fields only.
 * Numeric legacy values are deliberately rejected even when delivered as
 * strings, so missing/malformed canonical data stays neutral instead of
 * resurrecting Hygienist.status as UI authority.
 */
export function resolveCanonicalFfsPresentationCode(
  currentStatusCode: string | null | undefined,
  activeOverlayState?: string | null
): string | null {
  const overlayCode = recoveryOverlayToDisplayCode(activeOverlayState);
  if (overlayCode) return overlayCode;
  if (!currentStatusCode || /^-?\d+$/.test(currentStatusCode)) return null;
  return currentStatusCode;
}

/**
 * Validate a canonical FFS string code used by the colour/label maps.
 * Numeric values and numeric-looking strings are legacy compatibility data.
 */
export function normalizeFfsCode(
  code: string | null | undefined
): string | null {
  if (!code || /^-?\d+$/.test(code)) return null;
  return code;
}

/** Chip background colour for an FFS code or legacy int. */
export function getFfsStatusColor(code: string | null | undefined): string {
  const normalized = normalizeFfsCode(code);
  if (!normalized) return COLOR_FALLBACK;
  return STATUS_COLORS[normalized] || COLOR_FALLBACK;
}

/** Display label for the chip — the raw FFS code, or "—" when unknown. */
export function getFfsStatusLabel(code: string | null | undefined): string {
  const normalized = normalizeFfsCode(code);
  return normalized || "—";
}

/**
 * Optional warning accent icon (mdi). Returns null for codes that need no
 * accent (Active/Pro/Elite/Provisional/Deactivated).
 */
export function getFfsStatusIcon(
  code: string | null | undefined
): string | null {
  const normalized = normalizeFfsCode(code);
  if (!normalized) return null;
  if (normalized === "On a Break") return "mdi-pause-circle";
  if (normalized === "Not a Fit") return "mdi-account-off-outline"; // T2-6516
  if (STRONG_WARNING.has(normalized)) return "mdi-alert";
  if (MILD_WARNING.has(normalized)) return "mdi-alert-outline";
  return null;
}

/**
 * Whether the chip should use light text for contrast against its background.
 */
export function ffsStatusNeedsDarkText(
  code: string | null | undefined
): boolean {
  const normalized = normalizeFfsCode(code);
  if (!normalized) return true;
  // Amber and grey backgrounds read better with dark text.
  return (
    normalized === "CourseRequired" ||
    normalized === "Course Required" || // T2-6516 recovery overlay (amber)
    normalized === "Provisional" ||
    normalized === "Deactivated"
  );
}

/**
 * Colour-key rows for the Status-column popover (T2-6374 AC: "Colour key is
 * documented"). One entry per distinct FFS code.
 */
export const FFS_STATUS_COLOR_KEY: Array<{
  code: string;
  color: string;
  description: string;
}> = [
  { code: "Elite", color: COLOR_ELITE, description: "Top tier" },
  { code: "Pro", color: COLOR_PRO, description: "Pro tier" },
  { code: "Active", color: COLOR_ACTIVE, description: "Good standing" },
  {
    code: "RL1 / FL1 / RL1FL1",
    color: COLOR_ACTIVE,
    description: "Mild restriction (ranks with Active)"
  },
  {
    code: "RL2 / FL2 / RL2FL1 / RL1FL2 / RL2FL2",
    color: COLOR_PROBATION,
    description: "Severe restriction (ranks with Probation)"
  },
  {
    code: "CourseRequired",
    color: COLOR_COURSE_REQUIRED,
    description: "Recovery course assigned — action required"
  },
  {
    code: "On a Break",
    color: COLOR_ON_A_BREAK,
    description: "Active recovery: temporary break"
  },
  {
    code: "Restricted Re-entry",
    color: COLOR_RESTRICTED_REENTRY,
    description: "Active recovery: restricted re-entry"
  },
  {
    code: "Not a Fit",
    color: COLOR_NOT_A_FIT,
    description: "Active recovery: not a fit"
  },
  {
    code: "Probation",
    color: COLOR_PROBATION,
    description: "Probation (legacy)"
  },
  { code: "Suspended", color: COLOR_SUSPENDED, description: "Suspended" },
  { code: "Revoked", color: COLOR_REVOKED, description: "Revoked" },
  {
    code: "Provisional",
    color: COLOR_PROVISIONAL,
    description: "Under 5 completed shifts — insufficient data yet"
  },
  { code: "Deactivated", color: COLOR_DEACTIVATED, description: "Deactivated" }
];
